import { useState, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthUser } from '@/hooks/useAuth';
import { TARGETING_STYLES } from './constants';
import { FeedbackCategory, ElementMetadata } from './types';
import { useElementTargeting } from './hooks/useElementTargeting';
import { useConsoleErrorCapture } from './hooks/useConsoleErrorCapture';
import { useFeedbackSubmission } from './hooks/useFeedbackSubmission';
import { TargetingOverlay } from './TargetingOverlay';
import { FeedbackForm } from './FeedbackForm';

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
  const { user } = useAuthUser();
  const location = useLocation();
  const trayRef = useRef<HTMLDivElement>(null);

  const [showSuccess, setShowSuccess] = useState(false);
  const [category, setCategory] = useState<FeedbackCategory>('general');
  const [description, setDescription] = useState('');
  const [selectedElement, setSelectedElement] = useState<ElementMetadata | null>(null);

  const { getErrors } = useConsoleErrorCapture();

  const { isSubmitting, submitFeedback } = useFeedbackSubmission({
    pathname: location.pathname,
    user,
  });

  const handleElementSelected = useCallback((metadata: ElementMetadata) => {
    setSelectedElement(metadata);
  }, []);

  const { isTargeting, cursorPos, startTargeting, cancelTargeting } = useElementTargeting({
    dialogRef: trayRef,
    onElementSelected: handleElementSelected,
  });

  const wasTargetingRef = useRef(false);

  useEffect(() => {
    if (isTargeting) {
      wasTargetingRef.current = true;
    }
  }, [isTargeting]);

  const resetForm = useCallback(() => {
    setCategory('general');
    setDescription('');
    setSelectedElement(null);
    setShowSuccess(false);
  }, []);

  useEffect(() => {
    if (open) {
      if (wasTargetingRef.current) {
        wasTargetingRef.current = false;
        return;
      }
      resetForm();
    }
  }, [open, resetForm]);

  const closedByUserRef = useRef(false);

  const handleClose = useCallback(() => {
    closedByUserRef.current = true;
    cancelTargeting();
    onOpenChange(false);
    setTimeout(resetForm, 300);
  }, [onOpenChange, resetForm, cancelTargeting]);

  const handleStartTargeting = useCallback(() => {
    closedByUserRef.current = false;
    startTargeting();
    onOpenChange(false);
  }, [startTargeting, onOpenChange]);

  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => {
        handleClose();
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [showSuccess, handleClose]);

  const handleSubmit = useCallback(async () => {
    const success = await submitFeedback({
      category,
      description,
      selectedElement,
      consoleErrors: getErrors(),
    });
    if (success) {
      setShowSuccess(true);
    }
  }, [category, description, selectedElement, submitFeedback, getErrors]);

  // State machine for dialog open/close during targeting:
  // 1. User clicks "Select Element" -> handleStartTargeting:
  //    - closedByUserRef = false (reset for targeting flow)
  //    - isTargeting = true
  //    - open = false (dialog closes to allow targeting)
  // 2. User clicks an element -> selectElement:
  //    - selectedElement = metadata (set by onElementSelected)
  //    - isTargeting = false
  // 3. This effect fires: !isTargeting && selectedElement && !open && !closedByUserRef
  //    -> onOpenChange(true) (dialog reopens with selected element)
  // 4. User clicks "Close" -> handleClose:
  //    - closedByUserRef = true (prevents re-open effect)
  //    - open = false
  useEffect(() => {
    if (!isTargeting && selectedElement && !open && !closedByUserRef.current) {
      onOpenChange(true);
    }
  }, [isTargeting, selectedElement, open, onOpenChange]);

  return (
    <>
      <style>{TARGETING_STYLES}</style>

      {isTargeting && <TargetingOverlay cursorPos={cursorPos} onCancel={cancelTargeting} />}

      {open && (
        <div
          ref={trayRef}
          data-feedback-fab
          className="fixed bottom-20 right-6 z-[9989] w-[700px] rounded-lg bg-surface shadow-2xl border border-default p-5 animate-dialogIn"
        >
          <FeedbackForm
            category={category}
            description={description}
            selectedElement={selectedElement}
            isSubmitting={isSubmitting}
            showSuccess={showSuccess}
            onClose={handleClose}
            onCategoryChange={setCategory}
            onDescriptionChange={setDescription}
            onStartTargeting={handleStartTargeting}
            onSubmit={handleSubmit}
          />
        </div>
      )}
    </>
  );
}
